const LOCAL_DEMO_HOSTNAMES = new Set(['localhost', '127.0.0.1'])
const DEMO_USER_CHANGE_EVENT = 'codeindex:demo-user-change'

export const DEMO_USER_STORAGE_KEY = 'codeindex.demoUserEmail'
export const DEMO_USER_HEADER_NAME = 'X-Demo-User-Email'
export const DEMO_USER_QUERY_PARAM = 'demoUserEmail'

type DemoEnvironmentOptions = {
  hostname?: string
  nodeEnv?: string
}

type EffectiveDemoUserOptions = DemoEnvironmentOptions & {
  storage?: Storage | null
}

type DemoAwareUrlOptions = {
  demoUserEmail?: string | null
  enabled?: boolean
  origin?: string
}

function getBrowserWindow(): Window | null {
  return typeof window === 'undefined' ? null : window
}

function normalizeEmail(value: string | null | undefined): string | null {
  const normalized = String(value ?? '').trim().toLowerCase()
  return normalized || null
}

function readStoredDemoUserEmail(storage?: Storage | null): string | null {
  try {
    const resolvedStorage = storage ?? getBrowserWindow()?.localStorage ?? null
    if (!resolvedStorage) return null
    return normalizeEmail(resolvedStorage.getItem(DEMO_USER_STORAGE_KEY))
  } catch (_error) {
    return null
  }
}

function emitDemoUserChange(): void {
  const browserWindow = getBrowserWindow()
  if (!browserWindow) return

  browserWindow.dispatchEvent(new Event(DEMO_USER_CHANGE_EVENT))
}

export function isDemoUserEnvironment(options?: DemoEnvironmentOptions): boolean {
  const browserWindow = getBrowserWindow()
  const hostname = options?.hostname ?? browserWindow?.location.hostname ?? ''
  const nodeEnv = options?.nodeEnv ?? process.env.NODE_ENV

  return nodeEnv === 'development' && LOCAL_DEMO_HOSTNAMES.has(hostname)
}

export function getDemoUserEmail(options?: EffectiveDemoUserOptions): string | null {
  if (!isDemoUserEnvironment(options)) return null
  return readStoredDemoUserEmail(options?.storage)
}

export function getStoredDemoUserEmail(storage?: Storage | null): string | null {
  return readStoredDemoUserEmail(storage)
}

export function setDemoUserEmail(nextEmail: string | null | undefined): void {
  try {
    const browserWindow = getBrowserWindow()
    const storage = browserWindow?.localStorage
    if (!storage) return

    const normalizedEmail = normalizeEmail(nextEmail)
    if (normalizedEmail) {
      storage.setItem(DEMO_USER_STORAGE_KEY, normalizedEmail)
    } else {
      storage.removeItem(DEMO_USER_STORAGE_KEY)
    }

    emitDemoUserChange()
  } catch (_error) {
    // Ignore storage errors in local demo tooling.
  }
}

export function clearDemoUserEmail(): void {
  setDemoUserEmail(null)
}

export function getDemoUserRequestHeaderValue(options?: EffectiveDemoUserOptions): string | null {
  return options?.storage || options?.hostname || options?.nodeEnv
    ? getDemoUserEmail(options)
    : getDemoUserEmail()
}

export function buildDemoAwareUrl(baseUrl: string, options?: DemoAwareUrlOptions): string {
  const demoUserEmail = normalizeEmail(options?.demoUserEmail)
  if (!baseUrl || options?.enabled === false || !demoUserEmail) {
    return baseUrl
  }

  const browserWindow = getBrowserWindow()
  const origin = options?.origin ?? browserWindow?.location.origin ?? 'http://localhost'
  const url = new URL(baseUrl, origin)
  url.searchParams.set(DEMO_USER_QUERY_PARAM, demoUserEmail)
  return url.toString()
}

export function subscribeToDemoUserChanges(callback: () => void): () => void {
  const browserWindow = getBrowserWindow()
  if (!browserWindow) {
    return () => undefined
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === DEMO_USER_STORAGE_KEY) {
      callback()
    }
  }

  browserWindow.addEventListener(DEMO_USER_CHANGE_EVENT, callback as EventListener)
  browserWindow.addEventListener('storage', handleStorage)

  return () => {
    browserWindow.removeEventListener(DEMO_USER_CHANGE_EVENT, callback as EventListener)
    browserWindow.removeEventListener('storage', handleStorage)
  }
}
